/**
 * Client fetchery dla komponentu `c_polls` (ankiety).
 *
 * Wszystkie wywołania idą przez Next.js route handler proxy w `app/api/c-polls/*`,
 * które dokładają X-API-Key oraz (dla zalogowanego użytkownika frontendu) Bearer
 * token z sesji. Dzięki temu:
 *   - zalogowany user -> backend `user()` rozwiązany, głos przypisany do konta,
 *   - gość -> backend quick-rejestruje konto z podanych danych.
 *
 * Endpointy backendu (REST):
 *   - POST /IRESTAPI/v1/C_POLLS_CONTROLLER/savePoll/{id_poll}
 *   - POST /IRESTAPI/v1/C_POLLS_CONTROLLER/pollFillTime/{id_poll}
 *   - GET  /IRESTAPI/v1/C_POLLS_CONTROLLER/pollValidateEmail?email=
 */

import type { CPollQuestion } from "@/lib/api/cms-catalog";

/** Kolejny krok stronicowania zwracany przez `savePoll` (gdy `done=false`). */
export type CPollsStep = {
  questions: CPollQuestion[];
  progress: { question_count: number; filled_question: number };
  last_filled_id: number;
};

/** Wartość pojedynczego pytania w body zapisu (zależnie od typu pytania). */
export type CPollVoteValue = number | string | number[];

export type SavePollPayload = {
  /** ID ankiety (`m_polls.id`). */
  id_poll: number;
  /** ID katalogu c_polls (kontekst hrefów / powiadomienia / rejestracji gościa). */
  id_catalog: number;
  /** Mapa głosów: {questionId: answerId | [answerId,...] | tekst}. */
  votes: Record<string, CPollVoteValue>;
  /** Uzasadnienia per pytanie (tylko typ=1 z odpowiedzią justification). */
  justifications?: Record<string, string>;
  /** Dane gościa (gdy niezalogowany). */
  email?: string;
  name?: string;
  surname?: string;
  telephone?: string;
  /** ID rozpoczętego wypełnienia (kolejne kroki stronicowania). */
  filled_poll_id?: number | null;
  /** Wymuszone zakończenie (np. koniec czasu) - zapisuje częściowe + end_filled. */
  ignore?: boolean;
  /** Sekundy spędzone od ostatniego pingu (akumulowane do fill_time). */
  elapsed?: number;
};

export type SavePollResult =
  | {
      ok: true;
      /** Czy ankieta ukończona (wszystkie pytania oddane lub wymuszono). */
      done: boolean;
      /** ID wypełnienia (do przekazania w kolejnych krokach). */
      filled_poll_id: number;
      /** URL ekranu "dziękujemy" (gdy `done`), inaczej null. */
      completed_href: string | null;
      /** Pytania kolejnego kroku (gdy `done=false`), inaczej null. Klient renderuje
          następną partię BEZ re-fetcha (anonimowy pageBootstrap nie widziałby
          głosów bieżącego usera). */
      next_step: CPollsStep | null;
    }
  | {
      ok: false;
      /** Błędy walidacji per pole / pytanie. `votes` mapuje questionId -> kod. */
      errors?: {
        email?: string;
        name?: string;
        surname?: string;
        telephone?: string;
        votes?: Record<string, string>;
      };
      message?: string;
    };

/**
 * Zapis głosów ankiety (jeden krok lub całość).
 *
 * @param payload Dane zapisu (głosy, dane gościa, kontekst)
 * @returns Wynik zapisu (sukces z `done`/`completed_href` lub błędy walidacji)
 */
export async function savePoll(payload: SavePollPayload): Promise<SavePollResult> {
  const res = await fetch("/api/c-polls/save", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    cache: "no-store",
  });

  const body = (await res.json()) as SavePollResult;
  return body;
}

export type FillTimeResult = {
  ok: boolean;
  /** Pozostały czas w sekundach (null gdy ankieta bez limitu). */
  remain_time: number | null;
  /** `true` gdy czas minął - klient wymusza zapis z `ignore`. */
  end_time: boolean;
};

/**
 * Aktualizacja czasu wypełniania (limit czasowy). Persystuje postęp + zwraca
 * pozostały czas i flagę końca czasu.
 *
 * @param idPoll ID ankiety
 * @param filledPollId ID wypełnienia (null gdy jeszcze nie rozpoczęte)
 * @param elapsed Sekundy od ostatniego pingu
 */
export async function updateFillTime(
  idPoll: number,
  filledPollId: number | null,
  elapsed: number,
): Promise<FillTimeResult> {
  const res = await fetch("/api/c-polls/fill-time", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id_poll: idPoll,
      filled_poll_id: filledPollId,
      elapsed,
    }),
    cache: "no-store",
  });

  return (await res.json()) as FillTimeResult;
}

/**
 * Walidacja dostępności adresu e-mail (rejestracja gościa). `true` = wolny.
 *
 * @param email Adres e-mail do sprawdzenia
 */
export async function validatePollEmail(email: string): Promise<boolean> {
  const qs = `?email=${encodeURIComponent(email)}`;
  const res = await fetch(`/api/c-polls/validate-email${qs}`, {
    cache: "no-store",
  });
  if (!res.ok) return true; //fail-open - twarda walidacja i tak po stronie zapisu
  const body = (await res.json()) as { available?: boolean };
  return body.available !== false;
}
